<template lang="pug">
#ryourogue
  .header
    .main-container
      .contents
        h2 #RYOUROGUE
        p {{ __('Tag your photos on Instagram with #RYOUROGUE for a chance to be featured in our gallery.') }}
  .main-container
    .curations-grid
      .curation-tile(
        v-for="(curation, i) in curations"
        :key="curation.id"
        @click="toggleCurationModal(i)"
      )
        StaticImage.curation-image.image(:src="curation.image" :alt="'Post by ' +curation.author" :width="340")
        .overlay
          .time {{ timeDifference(curation.timestamp) }}
          Icon(name="instagram" fill="white")
    .show-more(v-show="after")
      RogueButton(
        color="white"
        :disabled="loading"
        @click="load"
      ) {{ __('Show More') }}
    CarouselModal(v-if="showRoguebox" @close="showRoguebox = false" :value="rogueboxCurrent" :useDefaultClose="false" ref="modal")
      Curation.roguebox-curation-slide(
        v-for="curation in curations"
        @close="$refs.modal.close()"
        :key="curation.id"
        :author="curation.author"
        :date="curation.date"
        :text="curation.text"
        :image="curation.image"
        :source="curation.source"
        :permalink="curation.permalink"
        :products="curation.products"
      )
        SocialShare(
          v-if="!$rogue.appContext"
          :shareData="{ text: curation.text, url: curation.permalink, image: curation.image }"
        )
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { CurationResponse } from './types'
import Curation from '@/js/components/curation.vue'
import SocialShare from '@/js/components/social-share.vue'
import { useRogue } from '@/js/services/Rogue'

type CurationItem = {
  id: string,
  image?: string,
  author: {
    username: string;
    link: string;
  },
  date: string,
  timestamp : number,
  products? : any[],
  text : string,
  source : string,
  permalink : string,
}

const rogue = useRogue()

const showRoguebox = ref<boolean>(false)
const rogueboxCurrent = ref<number | null>(null)
const curations = ref<CurationItem[]>([])
const after = ref<string | null>(null)
const loading = ref<boolean>(false)

onMounted(() => {
  load()
})

async function load() {
  loading.value = true

  try {
    let fetchUrl = '/svc_content/curations/list?limit=32'

    if (after.value) {
      fetchUrl += `&after=${after.value}`
    }

    const { items, after: respAfter } = await rogue.fetch<CurationResponse>(fetchUrl)

    curations.value = curations.value.concat((items || []).filter((f) => f.media?.largeSquare?.link || f.media?.poster?.largeSquare?.link).map((item) => ({
      id        : item.id,
      image     : item?.media?.largeSquare?.link || item?.media?.poster?.largeSquare?.link,
      author    : item.source.user,
      date      : new Date(item.source.postedTimestamp * 1000).toLocaleDateString(),
      timestamp : item.source.postedTimestamp * 1000,
      products  : item.products,
      text      : item.source.caption,
      source    : item.source.type,
      permalink : item.source.link,
    })))
    after.value = respAfter || null
  } catch (e) {
    rogue.log.error('Error loading curations from Curalate', e)
    curations.value = []
  }

  loading.value = false
}

function timeDifference(previous: number) {
  const current = Date.now()

  const msPerMinute = 60 * 1000
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365

  const elapsed = current - previous

  if (elapsed < msPerMinute) {
    return `${Math.round(elapsed / 1000)} ${rogue.__('seconds ago')}`
  }

  if (elapsed < msPerHour) {
    return `${Math.round(elapsed / msPerMinute)} ${rogue.__('minutes ago')}`
  }

  if (elapsed < msPerDay) {
    return `${Math.round(elapsed / msPerHour)} ${rogue.__('hours ago')}`
  }

  if (elapsed < msPerMonth) {
    return `${Math.round(elapsed / msPerDay)} ${rogue.__('days ago')}`
  }

  if (elapsed < msPerYear) {
    return `${Math.round(elapsed / msPerMonth)} ${rogue.__('months ago')}`
  }

  return `${Math.round(elapsed / msPerYear)} ${rogue.__('years ago')}`
}

function toggleCurationModal(index: number) {
  if (index !== undefined) {
    showRoguebox.value = true
    rogueboxCurrent.value = index
  } else {
    showRoguebox.value = false
  }
}
</script>

<style lang="scss" scoped>
.header {
  margin: $space-m 0;

  .contents {
    text-align: center;

    h2 {
      font-family: $font-condensed;
      font-size: clamp($size-xxxl, calc(5vw + 1rem), $size-xxxxxxl);
      margin-bottom: 0;
    }
  }

  @media (min-width: $tablet) {
    margin: 0 0 $space-l;
    background-image: url(https://assets.roguefitness.com/image/upload/f_auto,q_auto/v1737487643/theindex/Headers/ryourogue-hero_vrkq5n_egoydx.jpg);
    background-repeat: no-repeat;
    position: relative;
    background-size: 240%;
    background-position: center top;

    &:before {
      padding-bottom: 24%;
      display: block;
      content: '';
    }

    .main-container {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $color-white;
      padding-right: $space-m;

      .contents {
        width: 50%;
        max-width: 500px;
      }
    }
  }

  @include all-desktop() {
    background-size: 100%;

    &:before {
      padding-bottom: 15%;
    }
  }
}

.curations-grid {
  display: grid;
  grid-template-columns: 100%;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, calc(50% - 10px));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, calc(33.3333% - 13.3333px));
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, calc(25% - 15px));
  }
}

.curation-tile {
  position: relative;

  .overlay {
    position: absolute;
    bottom: 0;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    padding: $space-xs;
    color: $color-white;

    .time {
      font-size: $size-xs;
      margin-right: $space-xs;
    }
  }
}

.show-more {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: $space-l 0;

  &:before, &:after {
    content: '';
    background: $color-gray-light;
    height: 1px;
    flex-grow: 2;
  }

  .button {
    margin: 0 $space-l;
  }
}
</style>
